// utils/rememberEmail.js — Anamoria SPA
// v0.1 — NEW-93: opt-in "Remember my email on this device" (login_hint pre-fill)
//
// Device-global, on-device memory of the sign-in email, used to pre-fill the
// Auth0 identifier screen via the OIDC login_hint parameter (OpenID Connect
// Core 1.0). Stored ONLY when the user opts in ("Remember my email on this
// device", default OFF).
//
// Design notes:
//   - localStorage is the appropriate store (OWASP HTML5 Security Cheat Sheet:
//     non-sensitive, non-authenticating data). In a passwordless system the
//     email is an identifier, not a secret — the OTP still gates access. No
//     tokens are stored, so ADR-003 is untouched.
//   - Device-global (NOT per-user): read at the login screen before any user is
//     known, so it cannot be keyed by Auth0 sub.
//   - FAIL-OPEN: unavailable/throwing storage is swallowed; a missing hint only
//     means the email is not pre-filled, never a blocked sign-in.
//   - UNTRUSTED ON READ: the stored value is validated against a basic email
//     shape before use (guards localStorage poisoning, OWASP HTML5 CS); a
//     tampered value only changes what is pre-filled, never an auth outcome.

const FLAG_KEY = 'anamoria.rememberEmail';
const EMAIL_KEY = 'anamoria.lastEmail';

// Deliberately loose: this only decides whether a pre-fill hint is passed.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isRemembering = () => {
  try { return localStorage.getItem(FLAG_KEY) === '1'; }
  catch { return false; }
};

// Store the email only while opted in and only if it looks like an email.
export const setRememberedEmail = (email) => {
  try {
    if (isRemembering() && email && EMAIL_SHAPE.test(email)) {
      localStorage.setItem(EMAIL_KEY, email);
    }
  } catch { /* fail-open */ }
};

// Turn the opt-in on (storing the current email now) or off (clearing both).
export const setRemembering = (on, email) => {
  try {
    if (on) {
      localStorage.setItem(FLAG_KEY, '1');
      setRememberedEmail(email);
    } else {
      localStorage.removeItem(FLAG_KEY);
      localStorage.removeItem(EMAIL_KEY);
    }
  } catch { /* fail-open */ }
};

// A valid remembered email, or '' — safe to test before building a hint.
export const getRememberedEmail = () => {
  try {
    if (!isRemembering()) return '';
    const v = localStorage.getItem(EMAIL_KEY) || '';
    return EMAIL_SHAPE.test(v) ? v : '';
  } catch { return ''; }
};

// Convenience for loginWithRedirect: {} or { login_hint }. Spread into
// authorizationParams so an absent email leaves the request unchanged.
export const loginHintParams = () => {
  const email = getRememberedEmail();
  return email ? { login_hint: email } : {};
};
